import React, { useEffect, useCallback, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { useSelector, useDispatch } from 'react-redux';
import { SWITCH_CASH_FLOW_MODAL } from '../../../../../../redux/types';
import { RootState } from '../../../../../../redux/store';
import { groupBy } from '@progress/kendo-data-query';
import { setExpandedState, setGroupIds } from '@progress/kendo-react-data-tools';
import useClickOutside from '../../../../../../common/hooks/useClickOutside';
import { getCashflowData } from '../../../../../../common/services/http/analysisContentService';
import useTranslate from '../../../../../../common/hooks/useTranslate';
import { useParams } from 'react-router-dom';

import { Grid, GridColumn } from '@progress/kendo-react-grid';
import { ExcelExport } from '@progress/kendo-react-excel-export';
import AmountCell from '../cells/amountCell/amountCell';
import Icon from '../../../../../ui/icon/icon';
import DateCell from '../../../../../documents/components/cells/dateCell/dateCell';
import ComponentLoader from '../../../../../ui/loaders/componentLoader/componentLoader';
import TextCell from '../../../../../ui/table/cells/textCell/textCell';

import './cashFlowModal.scss';

const modalElement = document.getElementById('modal-root');

const initialGroup = [
  {
    field: 'objectName',
  },
];

const processWithGroups = (data, group) => {
  const newDataState = groupBy(data, group);
  setGroupIds({
    data: newDataState,
    group: group,
  });
  return newDataState;
};

export const CashFlowModal = () => {
  const [displayData, setDisplayData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [measureInfo, setMeasureInfo] = useState({});
  const [collapsedState, setCollapsedState] = React.useState([]);

  const { t } = useTranslate();

  const { investorId } = useParams();

  const dispatch = useDispatch();

  const modalRef = useRef(null);

  const showCashFlowModal = useSelector((state: RootState) => state.cashFlowModal.showModal);
  const data = useSelector((state: RootState) => state.cashFlowModal.data);

  const closeDialog = () => {
    dispatch({ type: SWITCH_CASH_FLOW_MODAL });
  };

  const handleEscape = useCallback(
    (event) => {
      if (event.keyCode === 27) closeDialog();
    },
    [closeDialog],
  );

  useEffect(() => {
    setLoading(true);
    if (data && data.cashFlowInfo) {
      getCashflowData(data.boardId, data.cashFlowInfo.nodeId, data.cashFlowInfo.valueId, investorId ? investorId : data.investorObjectId)
        .then((res) => {
          setDisplayData(processWithGroups(res.data, initialGroup));
          if (data.cashFlowInfo.valueInfo) {
            setMeasureInfo(data.cashFlowInfo.valueInfo.measure);
          }
          setLoading(false);
        })
        .catch((e) => console.log('cashflow error', e));
    }
    if (showCashFlowModal) document.addEventListener('keydown', handleEscape, false);
    return () => {
      document.removeEventListener('keydown', handleEscape, false);
      setLoading(true);
      setDisplayData([]);
      setMeasureInfo({});
      setCollapsedState([]);
    };
  }, [data, showCashFlowModal]);

  useClickOutside(modalRef, closeDialog);

  const onExpandChange = React.useCallback(
    (event) => {
      const item = event.dataItem;
      if (item.groupId) {
        const newCollapsedIds = !event.value ? [...collapsedState, item.groupId] : collapsedState.filter((groupId) => groupId !== item.groupId);
        setCollapsedState(newCollapsedIds);
      }
    },
    [collapsedState],
  );

  const newData = setExpandedState({
    data: displayData,
    collapsedIds: collapsedState,
  });

  const FormattedAmountCell = (props) => {
    return <AmountCell {...props} measureInfo={measureInfo} />;
  };

  // Export to Excel
  const _exportGrid = React.useRef(null);

  const gridExport = (): void => {
    if (_exportGrid.current !== null) {
      _exportGrid.current.save();
    }
  };

  const amountTitle = () => {
    let currency = data.selectedCurrency && (data.selectedCurrency.currency === '_' ? data.dataItem.currency : `${data.selectedCurrency.currencySymbol}${data.selectedCurrency.currencyShortName}`);
    return t('BOARDS.GRID_FIELD.AMOUNT') + ' ' + currency;
  }

  return createPortal(
    showCashFlowModal ? (
      <div className={`modal-cash-flow`}>
        <div className='modal-body' ref={modalRef}>
          <div className={'modal-title'}>
            <div className='cashflow-header'>
              {data.dataItem && data.dataItem.name && <span className='modal-title-span'>{data.dataItem.name}</span>}
              {data.columnName && <span className='modal-title-span'>{data.columnName}:</span>}
              {data.value && <span className='modal-title-span'>{data.value}</span>}
              {data.selectedCurrency && <span
                className='modal-title-span'>{data.selectedCurrency.currencySymbol}{data.selectedCurrency.currencyShortName}</span>
              }
              {data.selectedCurrency && data.selectedCurrency.currency === '_' && <span className='modal-title-span'>{data.dataItem.currency}</span>}
            </div>
            <div>
              <Icon onClick={gridExport} name={'far fa-file-excel '}
                    className={'pointer export-icon export-icon hidden-mobile'} />
              <Icon name='fas fa-xmark' className='impersonate-off-icon pointer' onClick={closeDialog} />
            </div>
          </div>
          <div className='cashflow-grid-wrapper'>
            {!loading ? (
              <ExcelExport data={newData} ref={_exportGrid} group={initialGroup}>
                <Grid data={newData} resizable={true} groupable={true} onExpandChange={onExpandChange}
                      group={initialGroup} expandField='expanded'>
                  <GridColumn
                    field='objectName'
                    title={t('BOARDS.GRID_FIELD.OBJECT_NAME')}
                    width={300}
                    minResizableWidth={100}
                    cell={(props) => <TextCell {...props} grid />}
                  />
                  <GridColumn
                    field='assignedObjectName'
                    title={t('BOARDS.GRID_FIELD.ASSIGNED_OBJECT_NAME')}
                    width={275}
                    minResizableWidth={100}
                    cell={(props) => <TextCell {...props} grid />}
                  />
                  <GridColumn field='date' title={t('BOARDS.GRID_FIELD.DATE')} cell={DateCell} width={150}
                              minResizableWidth={100} />
                  <GridColumn field='amount'
                              title={amountTitle()}
                              cell={FormattedAmountCell}
                              width={180} minResizableWidth={100} />
                  <GridColumn
                    field='eventName'
                    title={t('BOARDS.GRID_FIELD.EVENT_NAME')}
                    width={300}
                    minResizableWidth={100}
                    cell={(props) => <TextCell {...props} grid />}
                  />
                  <GridColumn
                    field='itemName'
                    title={t('BOARDS.GRID_FIELD.ITEM_NAME')}
                    width={300}
                    minResizableWidth={100}
                    cell={(props) => <TextCell {...props} grid />}
                  />
                  <GridColumn
                    field='typeName'
                    title={t('BOARDS.GRID_FIELD.TYPE_NAME')}
                    width={270}
                    minResizableWidth={100}
                    cell={(props) => <TextCell {...props} grid />}
                  />
                  <GridColumn
                    field='accountName'
                    title={t('BOARDS.GRID_FIELD.ACCOUNT_NAME')}
                    width={270}
                    minResizableWidth={100}
                    cell={(props) => <TextCell {...props} grid />}
                  />
                  <GridColumn
                    field='rate'
                    title={t('BOARDS.GRID_FIELD.RATE')}
                    width={150}
                    minResizableWidth={100}
                    cell={(props) => <TextCell {...props} grid />}
                  />
                  <GridColumn
                    field='sourceCurrency'
                    title={t('BOARDS.GRID_FIELD.SOURCE_CURRENCY')}
                    width={150}
                    minResizableWidth={100}
                    cell={(props) => <TextCell {...props} grid />}
                  />
                  <GridColumn
                    field='rateDate'
                    title={t('BOARDS.GRID_FIELD.RATE_DATE')}
                    width={150}
                    minResizableWidth={100}
                    cell={(props) => <TextCell {...props} grid />}
                  />
                </Grid>
              </ExcelExport>
            ) : (
              <div className='component-loader-wrapper'>
                <ComponentLoader />
              </div>
            )}
          </div>
        </div>
      </div>
    ) : null,
    modalElement,
  );
};

export default CashFlowModal;
